import { Injectable } from '@angular/core';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import * as am5exporting from '@amcharts/amcharts5/plugins/exporting';
import { GraphType, ExportType } from '../../views/report-details/interfaces/report-details.interface';

/**
 * Base Chart Service - Common functionality for all chart types
 * Modified by: Anil Kumar boddu | 04-08-2025 
 * @author: PRachethan | 02-08-2025 
 */
@Injectable({
  providedIn: 'root'
})
export class BaseChartService {

  constructor() {
    // ✅ Set the license key once, globally
    am5.addLicense('AM5C-228909170');

  }


  // Global chart registry to track active charts
  private static chartRegistry: Map<string, { root: am5.Root; exporting: any; data: any[] }> = new Map();

  /**
   * Register a chart with its exporting instance and data
   */
  static registerChart(chartId: string, root: am5.Root, exporting: any, data?: any[]): void {
    BaseChartService.chartRegistry.set(chartId, { root, exporting, data: data || [] });
  }

  /**
   * Unregister a chart
   */
  static unregisterChart(chartId: string): void {
    BaseChartService.chartRegistry.delete(chartId);
  }

  /**
   * Get the most recent active chart
   */
  static getActiveChart(): { root: am5.Root; exporting: any; data: any[] } | null {
    const entries = Array.from(BaseChartService.chartRegistry.entries());
    return entries.length > 0 ? entries[entries.length - 1][1] : null;
  }

  /**
   * Update the data for the most recent active chart
   */
  static updateActiveChartData(data: any[]): void {
    const entries = Array.from(BaseChartService.chartRegistry.entries());
    if (entries.length > 0) {
      const lastEntry = entries[entries.length - 1];
      const chartInfo = lastEntry[1];
      chartInfo.data = data;
      BaseChartService.chartRegistry.set(lastEntry[0], chartInfo);
    }
  }



  /**
   * Attach a consistent export menu to any chart/root
   */
  attachExporting(root: am5.Root, options: { filePrefix?: string; data?: any[] } = {}): void {
    const { filePrefix = 'chart', data = [] } = options;

    const menu = am5exporting.ExportingMenu.new(root, {});
    const exporting = am5exporting.Exporting.new(root, {
      menu,
      filePrefix
    });
    // Register the chart with the global registry
    const chartId = `chart-${Date.now()}`;
    BaseChartService.registerChart(chartId, root, exporting, data);
  }


  exportGraph(selectedGraphItem: GraphType, type: ExportType) {

    // Get the current chart from registry
    const activeChart = BaseChartService.getActiveChart();

    if (!activeChart) {
      console.error('No active chart found for export');
      return;
    }

    // Try to get exporting from root if direct access fails
    let exportingInstance = activeChart.exporting;
    if (!exportingInstance || !exportingInstance.download) {
      // Try to find exporting in root children
      const root = activeChart.root;
      if (root && (root as any).children) {
        for (let i = 0; i < (root as any).children.length; i++) {
          const child = (root as any).children.getIndex(i);
          if (child && (child as any).download) {
            exportingInstance = child;

            break;
          }
        }
      }
    }

    // Handle different export types
    switch (type) {
      case 'png':
        this.exportAsImage(exportingInstance, 'png');
        break;
      case 'pdf':
        this.exportAsImage(exportingInstance, 'pdf');
        break;
      case 'svg':
        this.exportAsImage(exportingInstance, 'svg');
        break;
      case 'jpg':
        this.exportAsImage(exportingInstance, 'jpg');
        break;
      case 'json':
        this.exportAsData(exportingInstance, 'json');
        break;
      case 'csv':
        this.exportAsData(exportingInstance, 'csv');
        break;
      case 'xlsx':
        this.exportAsData(exportingInstance, 'xlsx');
        break;
      case 'html':
        this.exportAsData(exportingInstance, 'html');
        break;
      case 'print':
        this.printChart(exportingInstance);
        break;
      default:
        console.warn(`Export type '${type}' not supported for charts`);
    }
  }

  /**
   * Export chart as image
   */
  private exportAsImage(exporting: any, format: 'png' | 'pdf' | 'svg' | 'jpg'): void {
    if (exporting) {
      const fileName = `chart-export-${Date.now()}.${format}`;
      try {
        // Try different ways to access the download method
        if (exporting.download) {
          exporting.download(format, {
            fileName: fileName
          });
        } else if (exporting.export && exporting.export.download) {
          exporting.export.download(format, {
            fileName: fileName
          });
        } else if (exporting.menu && exporting.menu.export) {
          // Try using the menu's export functionality
          exporting.menu.export(format, {
            fileName: fileName
          });
        } else {
          console.error('Download method not found on exporting instance');
          // console.log('Available methods:', Object.getOwnPropertyNames(exporting));
        }
      } catch (error) {
        console.error('Error exporting image:', error);
      }
    } else {
      console.error('Exporting plugin not available');
    }
  }

  /**
   * Export chart data
   */
  private exportAsData(exporting: any, format: 'json' | 'csv' | 'xlsx' | 'html'): void {
    if (exporting) {
      try {
        // Get the active chart from registry to access the data
        const activeChart = BaseChartService.getActiveChart();
        if (!activeChart || !activeChart.root) {
          console.error('No active chart found for data export');
          return;
        }

        // Extract chart data
        const chartData = this.extractChartData(activeChart.root);
        if (!chartData || chartData.length === 0) {
          console.error('No data found in chart for export');
          return;
        }

        const fileName = `chart-data-${Date.now()}.${format}`;

        switch (format) {
          case 'json':
            this.exportAsJSON(chartData, fileName);
            break;
          case 'csv':
            this.exportAsCSV(chartData, fileName);
            break;
          case 'xlsx':
            this.exportAsXLSX(chartData, fileName);
            break;
          case 'html':
            this.exportAsHTML(chartData, fileName);
            break;
          default:
            console.error(`Unsupported data format: ${format}`);
        }
      } catch (error) {
        console.error('Error exporting data:', error);
      }
    } else {
      console.error('Exporting plugin not available');
    }
  }

  /**
   * Extract data from the chart
   */
  private extractChartData(root: am5.Root): any[] {
    try {
      // First, try to get data from the registry (most reliable)
      const activeChart = BaseChartService.getActiveChart();
      if (activeChart && activeChart.data && activeChart.data.length > 0) {
        return activeChart.data;
      }

      // Second, try to get data from the global chart state
      const globalData = this.getGlobalChartData();
      if (globalData && globalData.length > 0) {
        return globalData;
      }

      // Fallback: Try to find the chart in the root
      const chart = this.findChartInRoot(root);
      if (!chart) {
        console.error('No chart found in root');
        return [];
      }

      // Get series data
      const seriesData: any[] = [];

      if (chart.series && chart.series.values) {
        chart.series.values.forEach((series: any) => {
          if (series.data && series.data.values) {
            series.data.values.forEach((dataItem: any) => {
              const item: any = {};

              // Extract category (X-axis value)
              if (dataItem.categoryX !== undefined) {
                item.category = dataItem.categoryX;
              }

              // Extract value (Y-axis value)
              if (dataItem.valueY !== undefined) {
                item.value = dataItem.valueY;
              }

              // Extract series name
              if (series.name) {
                item.series = series.name;
              }

              // Extract other properties
              if (dataItem.dataContext) {
                Object.assign(item, dataItem.dataContext);
              }

              seriesData.push(item);
            });
          }
        });
      }

      // If no series data, try to get data from axes
      if (seriesData.length === 0) {
        const categoryAxis = chart.xAxes?.getIndex(0);
        if (categoryAxis && categoryAxis.data && categoryAxis.data.values) {
          categoryAxis.data.values.forEach((dataItem: any) => {
            const item: any = {};
            if (dataItem.categoryX !== undefined) {
              item.category = dataItem.categoryX;
            }
            if (dataItem.dataContext) {
              Object.assign(item, dataItem.dataContext);
            }
            seriesData.push(item);
          });
        }
      }

      return seriesData;
    } catch (error) {
      console.error('Error extracting chart data:', error);
      return [];
    }
  }

  /**
   * Get global chart data from the service state
   */
  private getGlobalChartData(): any[] {
    try {
      // Try to access the global chart state through the common service
      // This would require injecting the common service, but for now we'll try a different approach

      // Look for chart data in the DOM or global variables
      const chartContainers = document.querySelectorAll('[id*="chart"]');
      for (const container of chartContainers) {
        const chartData = (container as any).chartData;
        if (chartData && Array.isArray(chartData) && chartData.length > 0) {
          return chartData;
        }
      }

      return [];
    } catch (error) {
      console.error('Error getting global chart data:', error);
      return [];
    }
  }

  /**
   * Find chart in root
   */
  private findChartInRoot(root: am5.Root): any {
    try {
      // Try to find XYChart in root container
      if (root.container && root.container.children) {
        for (let i = 0; i < root.container.children.length; i++) {
          const child = root.container.children.getIndex(i);
          if (child && (child as any).series) {
            return child;
          }
        }
      }

      // Try to find chart in root children
      if ((root as any).children) {
        for (let i = 0; i < (root as any).children.length; i++) {
          const child = (root as any).children.getIndex(i);
          if (child && (child as any).series) {
            return child;
          }
        }
      }

      return null;
    } catch (error) {
      console.error('Error finding chart in root:', error);
      return null;
    }
  }

  /**
   * Export data as JSON
   */
  private exportAsJSON(data: any[], fileName: string): void {
    const jsonContent = JSON.stringify(data, null, 2);
    this.downloadFile(jsonContent, fileName, 'application/json');
  }

  /**
   * Export data as CSV
   */
  private exportAsCSV(data: any[], fileName: string): void {
    if (data.length === 0) {
      console.error('No data to export as CSV');
      return;
    }

    // Get headers from first object
    const headers = Object.keys(data[0]);

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...data.map(row =>
        headers.map(header => {
          const value = row[header];
          // Escape quotes and wrap in quotes if contains comma or quote
          const escapedValue = String(value).replace(/"/g, '""');
          return escapedValue.includes(',') || escapedValue.includes('"') || escapedValue.includes('\n')
            ? `"${escapedValue}"`
            : escapedValue;
        }).join(',')
      )
    ].join('\n');

    this.downloadFile(csvContent, fileName, 'text/csv');
  }

  /**
   * Export data as XLSX
   */
  private exportAsXLSX(data: any[], fileName: string): void {
    if (data.length === 0) {
      console.error('No data to export as XLSX');
      return;
    }

    // For XLSX, we'll create a simple HTML table that Excel can open
    const headers = Object.keys(data[0]);

    const htmlContent = `
      <html>
        <head>
          <meta charset="utf-8">
          <title>Chart Data</title>
        </head>
        <body>
          <table border="1">
            <thead>
              <tr>
                ${headers.map(header => `<th>${header}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${data.map(row =>
      `<tr>${headers.map(header => `<td>${row[header]}</td>`).join('')}</tr>`
    ).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    this.downloadFile(htmlContent, fileName, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
  }

  /**
   * Export data as HTML
   */
  private exportAsHTML(data: any[], fileName: string): void {
    if (data.length === 0) {
      console.error('No data to export as HTML');
      return;
    }

    const headers = Object.keys(data[0]);

    const htmlContent = `
      <html>
        <head>
          <meta charset="utf-8">
          <title>Chart Data</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            tr:nth-child(even) { background-color: #f9f9f9; }
          </style>
        </head>
        <body>
          <h1>Chart Data Export</h1>
          <table>
            <thead>
              <tr>
                ${headers.map(header => `<th>${header}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${data.map(row =>
      `<tr>${headers.map(header => `<td>${row[header]}</td>`).join('')}</tr>`
    ).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    this.downloadFile(htmlContent, fileName, 'text/html');
  }

  /**
   * Download file helper
   */
  private downloadFile(content: string, fileName: string, mimeType: string): void {
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Print chart
   */
  private printChart(exporting: any): void {
    if (exporting) {
      try {
        // Try different ways to access the print method
        if (exporting.print) {
          exporting.print();
        } else if (exporting.export && exporting.export.print) {
          exporting.export.print();
        } else {
          console.error('Print method not found on exporting instance');
        }
      } catch (error) {
        console.error('Error printing chart:', error);
      }
    } else {
      console.error('Exporting plugin not available');
    }
  }
  /**
   * Create root element with common configuration
   * @param containerId - Chart container ID
   * @returns am5.Root instance
   */
  createRoot(containerId: string): am5.Root {
    const root = am5.Root.new(containerId);

    // This is where you indicate you have a commercial license.
    // You do not place a key string here like in older versions.
    // The license is typically tied to the version of the files you downloaded.

    // Set themes like legacy implementation
    root.setThemes([
      am5themes_Animated.new(root)
    ]);

    return root;
  }

  /**
   * Enhanced animation configuration for series
   * @param series - Chart series to animate
   * @param animationType - Type of animation
   */
  setupSeriesAnimation(series: any, animationType: 'fade' | 'slide' | 'bounce' | 'elastic' = 'fade'): void {
    // Configure initial animation
    series.set('appearAnimation', animationType);
    series.set('appearDuration', 1000);
    series.set('appearEasing', am5.ease.out(am5.ease.cubic));

    // Configure hover animations
    series.columns?.template?.states?.create('hover', {
      scaleX: 1.05,
      scaleY: 1.05,
      duration: 200,
      easing: am5.ease.out(am5.ease.cubic)
    });

    series.bullets?.template?.states?.create('hover', {
      scale: 1.2,
      duration: 200,
      easing: am5.ease.out(am5.ease.cubic)
    });

    // Configure slice animations for pie charts
    series.slices?.template?.states?.create('hover', {
      scale: 1.1,
      duration: 200,
      easing: am5.ease.out(am5.ease.cubic)
    });
  }

  /**
   * Enhanced chart animation configuration
   * @param chart - Chart instance to animate
   * @param animationType - Type of animation
   */
  setupChartAnimation(chart: any, animationType: 'fade' | 'slide' | 'bounce' | 'elastic' = 'fade'): void {
    // Configure chart appearance animation
    chart.set('appearAnimation', animationType);
    chart.set('appearDuration', 1200);
    chart.set('appearEasing', am5.ease.out(am5.ease.cubic));

    // Configure legend animation
    const legend = chart.children.getIndex(1); // Assuming legend is the second child
    if (legend) {
      legend.set('appearAnimation', 'fade');
      legend.set('appearDuration', 800);
      legend.set('appearEasing', am5.ease.out(am5.ease.cubic));
    }
  }

  /**
   * Enhanced axis animation configuration
   * @param axis - Axis to animate
   */
  setupAxisAnimation(axis: any): void {
    // Configure axis appearance animation
    axis.set('appearAnimation', 'slide');
    axis.set('appearDuration', 800);
    axis.set('appearEasing', am5.ease.out(am5.ease.cubic));

    // Configure grid animations - only if grids exist
    if (axis.grids && axis.grids.template) {
      axis.grids.template.set('appearAnimation', 'fade');
      axis.grids.template.set('appearDuration', 600);
      axis.grids.template.set('appearEasing', am5.ease.out(am5.ease.cubic));
    }
  }

  /**
   * Enhanced legend animation configuration
   * @param legend - Legend to animate
   */
  setupLegendAnimation(legend: any): void {
    legend.set('appearAnimation', 'fade');
    legend.set('appearDuration', 600);
    legend.set('appearEasing', am5.ease.out(am5.ease.cubic));

    // Configure legend item animations
    legend.markers.template.states.create('hover', {
      scale: 1.2,
      duration: 200,
      easing: am5.ease.out(am5.ease.cubic)
    });
  }

  /**
   * Enhanced tooltip animation configuration
   * @param tooltip - Tooltip to animate
   */
  setupTooltipAnimation(tooltip: any): void {
    tooltip.set('appearAnimation', 'fade');
    tooltip.set('appearDuration', 300);
    tooltip.set('appearEasing', am5.ease.out(am5.ease.cubic));
  }

  /**
   * Create standardized tooltip with value and label display
   * @param root - am5.Root instance
   * @param chartType - Type of chart (bar, line, pie, column, etc.)
   * @param options - Additional tooltip options
   * @returns Configured tooltip
   */
  createStandardTooltip(root: am5.Root, chartType: string, options: {
    showPercentage?: boolean;
    showValue?: boolean;
    showCategory?: boolean;
    showSeries?: boolean;
    customFormat?: string;
  } = {}): am5.Tooltip {
    const {
      showPercentage = false,
      showValue = true,
      showCategory = true,
      showSeries = true,
      customFormat
    } = options;

    let labelText = '';

    switch (chartType.toLowerCase()) {
      case 'bar':
      case 'column':
        labelText = showSeries ? '{name}\n' : '';
        labelText += showCategory ? '{categoryX}: ' : '';
        labelText += showValue ? '{valueY.formatNumber("#,##0")}' : '';
        break;

      case 'line':
        labelText = showSeries ? '{name}\n' : '';
        labelText += showCategory ? '{categoryX}: ' : '';
        labelText += showValue ? '{valueY.formatNumber("#,##0")}' : '';
        break;

      case 'pie':
      case 'donut':
        labelText = showCategory ? '{category}: ' : '';
        if (showPercentage) {
          labelText += '{valuePercentTotal.formatNumber("0.00")}%\n';
        }
        labelText += showValue ? '{value.formatNumber("#,##0")}' : '';
        break;

      case 'area':
      case 'stacked-area':
        labelText = showSeries ? '{name}\n' : '';
        labelText += showCategory ? '{categoryX}: ' : '';
        if (showPercentage) {
          labelText += '{valueYTotalPercent.formatNumber("0.00")}% ';
        }
        labelText += showValue ? '({valueY.formatNumber("#,##0")})' : '';
        break;

      case 'stacked-column':
        labelText = showSeries ? '{name}\n' : '';
        labelText += showCategory ? '{categoryX}: ' : '';
        if (showPercentage) {
          labelText += '{valueYTotalPercent.formatNumber("0.00")}% ';
        }
        labelText += showValue ? '({valueY.formatNumber("#,##0")})' : '';
        break;

      case 'pyramid':
      case 'funnel':
        labelText = showCategory ? '{category}: ' : '';
        if (showPercentage) {
          labelText += '{valuePercentTotal.formatNumber("0.00")}% ';
        }
        labelText += showValue ? '({value.formatNumber("#,##0")})' : '';
        break;

      case 'lollipop':
        labelText = showSeries ? '{name}\n' : '';
        labelText += showCategory ? '{categoryX}: ' : '';
        labelText += showValue ? '{valueY.formatNumber("#,##0")}' : '';
        break;

      case 'timeline':
      case 'spiral-timeline':
      case 'linear-timeline':
      case 'road-timeline':
      case 'serpentine-timeline':
      case 'fishbone-timeline':
        labelText = showCategory ? '{category}: ' : '';
        labelText += '{start} - {end}';
        break;

      case 'nested-donut':
        labelText = showValue ? '{value.formatNumber("#,##0")} ' : '';
        if (showPercentage) {
          labelText += '({valuePercentTotal.formatNumber("0.00")}%)';
        }
        break;

      default:
        labelText = showSeries ? '{name}\n' : '';
        labelText += showCategory ? '{categoryX}: ' : '';
        labelText += showValue ? '{valueY.formatNumber("#,##0")}' : '';
    }

    // Use custom format if provided
    if (customFormat) {
      labelText = customFormat;
    }

    const tooltip = am5.Tooltip.new(root, {
      labelText: labelText,
      pointerOrientation: "horizontal",
      getFillFromSprite: false,
      background: am5.Rectangle.new(root, {
        fill: am5.color(0xFFFFFF),
        fillOpacity: 0.95,
        stroke: am5.color(0xE0E0E0),
        strokeWidth: 1
      })
    });

    this.setupTooltipAnimation(tooltip);
    return tooltip;
  }

  /**
   * Create chart container with common configuration
   * @param root - am5.Root instance
   * @param options - Chart options
   * @returns am5xy.XYChart instance
   */
  createChartContainer(root: am5.Root, options: {
    panX?: boolean;
    panY?: boolean;
    wheelX?: 'zoomX' | 'zoomY' | 'zoomXY' | 'panX' | 'panY' | 'panXY' | 'none';
    wheelY?: 'zoomX' | 'zoomY' | 'zoomXY' | 'panX' | 'panY' | 'panXY' | 'none';
    layout?: any;
  } = {}): am5xy.XYChart {
    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: options.panX ?? false,
        panY: options.panY ?? false,
        wheelX: options.wheelX ?? 'none',
        wheelY: options.wheelY ?? 'none',
        layout: options.layout
      })
    );

    return chart;
  }

  /**
   * Set up color palette for consistent colors
   * @param chart - am5xy.XYChart instance
   */
  setupColorPalette(chart: am5xy.XYChart): void {
    chart.set('colors', am5.ColorSet.new(chart.root, {
      colors: [
        am5.color(0x67B7DC), // Blue
        am5.color(0x6794DC), // Dark Blue
        am5.color(0x67DC75), // Green
        am5.color(0xDC7567), // Red
        am5.color(0xDCB767), // Yellow
        am5.color(0xDC67B7), // Pink
        am5.color(0x7567DC), // Purple
        am5.color(0x67DCD4), // Cyan
        am5.color(0xDCD467), // Lime
        am5.color(0xDC7567)  // Orange
      ]
    }));
  }

  /**
   * Create category axis with common configuration
   * @param chart - am5xy.XYChart instance
   * @param categoryField - Category field name
   * @param options - Axis options
   * @returns am5xy.CategoryAxis instance
   */
  createCategoryAxis(chart: am5xy.XYChart, categoryField: string, options: {
    axisType?: 'x' | 'y';
    minGridDistance?: number;
    cellStartLocation?: number;
    cellEndLocation?: number;
    rotation?: number;
  } = {}): am5xy.CategoryAxis<any> {


    const isXAxis = options.axisType !== 'y';
    const axis = isXAxis ?
      chart.xAxes.push(am5xy.CategoryAxis.new(chart.root, {
        categoryField: categoryField,
        renderer: am5xy.AxisRendererX.new(chart.root, {
          minGridDistance: options.minGridDistance ?? 50,
          cellStartLocation: options.cellStartLocation ?? 0.1,
          cellEndLocation: options.cellEndLocation ?? 0.9
        })
      })) :
      chart.yAxes.push(am5xy.CategoryAxis.new(chart.root, {
        categoryField: categoryField,
        renderer: am5xy.AxisRendererY.new(chart.root, {
          minGridDistance: options.minGridDistance ?? 30
        })
      }));

    if (isXAxis) {
      // Get the labels template and set the rotation
      axis.get("renderer").labels.template.setAll({
        rotation: options.rotation ?? 0,
        centerY: am5.p50,
        centerX: am5.p0,
        maxWidth: 150,
        maxChars: 20,
        breakWords: true,
        oversizedBehavior: 'truncate'
      });
    }

    // Configure axis properties
    axis.set('maxZoomFactor', 1);

    // Configure labels for diagonal text direction if rotation is specified
    if (options.rotation !== undefined) {
      this.configureAxisLabels(axis, options.rotation);
    }

    return axis;
  }

  /**
   * Create value axis with common configuration
   * @param chart - am5xy.XYChart instance
   * @param options - Axis options
   * @returns am5xy.ValueAxis instance
   */
  createValueAxis(chart: am5xy.XYChart, options: {
    axisType?: 'x' | 'y';
    title?: string;
  } = {}): am5xy.ValueAxis<any> {
    const isXAxis = options.axisType === 'x';
    const axis = isXAxis ?
      chart.xAxes.push(am5xy.ValueAxis.new(chart.root, {
        renderer: am5xy.AxisRendererX.new(chart.root, {})
      })) :
      chart.yAxes.push(am5xy.ValueAxis.new(chart.root, {
        renderer: am5xy.AxisRendererY.new(chart.root, {})
      }));

    // Configure axis title if provided
    if (options.title) {
      (axis as any).set('title', am5.Label.new(chart.root, { text: options.title }));
    }

    return axis;
  }

  /**
   * Configure axis labels for diagonal text direction
   * @param axis - Category axis instance
   * @param rotation - Rotation angle in degrees
   */
  private configureAxisLabels(axis: am5xy.CategoryAxis<any>, rotation: number): void {
    setTimeout(() => {
      try {
        // Try to find the axis renderer through the chart's axes
        const chart = axis.chart;
        if (chart) {
          const axes = (chart as any).xAxes;
          if (axes && axes.length > 0) {
            const foundAxis = axes[0];
            if (foundAxis && foundAxis.renderer && foundAxis.renderer.labels && foundAxis.renderer.labels.template) {
              foundAxis.renderer.labels.template.set('rotation', rotation);
              foundAxis.renderer.labels.template.set('horizontalCenter', 'right');
              foundAxis.renderer.labels.template.set('verticalCenter', 'middle');
              foundAxis.renderer.labels.template.set('maxWidth', 150);
              foundAxis.renderer.labels.template.set('truncate', true);
              // console.log('Labels configured through chart axes');
            }
          }
        }
      } catch (error) {
        console.warn('Chart axes approach failed:', error);
      }
    }, 300);
  }

  /**
   * Get the category field (first string field) from the data
   * @param data - The chart data
   * @returns The category field name
   */
  getCategoryField(data: any[]): string {
    if (!data || data.length === 0) {
      console.warn('No data provided to getCategoryField');
      return 'category';
    }

    const firstItem = data[0];
    const keys = Object.keys(firstItem);

    // console.log('Available fields in data:', keys);
    // console.log('First item sample:', firstItem);

    // Find the first string field (non-numeric) that's not empty
    for (const key of keys) {
      const value = firstItem[key];
      // console.log(`Checking field "${key}" with value:`, value, 'type:', typeof value);

      if (typeof value === 'string' && value && value.trim() !== '') {
        // console.log(`Selected category field: "${key}" with value: "${value}"`);
        return key;
      }
    }

    // If no string field found, look for any non-numeric field
    for (const key of keys) {
      const value = firstItem[key];
      if (typeof value !== 'number') {
        // console.log(`Selected non-numeric category field: "${key}" with value:`, value);
        return key;
      }
    }

    // Fallback to first key
    const fallbackKey = keys[0] || 'category';
    // console.log(`Using fallback category field: "${fallbackKey}"`);
    return fallbackKey;
  }

  /**
   * Get all value fields (numeric fields) from the data
   * @param data - The chart data
   * @returns Array of value field names
   */
  getValueFields(data: any[]): string[] {
    if (!data || data.length === 0) {
      return ['value'];
    }

    const firstItem = data[0];
    const keys = Object.keys(firstItem);
    const valueFields: string[] = [];

    // Find all numeric fields
    for (const key of keys) {
      const value = firstItem[key];
      if (typeof value === 'number') {
        valueFields.push(key);
      }
    }

    // If no numeric fields found, use fallback
    if (valueFields.length === 0) {
      const fallbackKey = keys.length > 1 ? keys[1] : keys[0];
      valueFields.push(fallbackKey || 'value');
    }

    return valueFields;
  }

  /**
   * Get single value field (first numeric field) from the data
   * @param data - The chart data
   * @returns The value field name
   */
  getValueField(data: any[]): string {
    const valueFields = this.getValueFields(data);
    return valueFields[0] || 'value';
  }

  /**
   * Transform report data to the format expected by charts
   * @param state - The state containing report data and column information
   * @returns Transformed data for the chart
   */
  transformReportData(state: any): any[] {
    try {
      if (!state?.reportData?.length || !state?.allColumnsData || !state?.reportFormData?.columns) {
        console.warn('Missing required data for transformation:', {
          hasReportData: !!state?.reportData?.length,
          hasAllColumnsData: !!state?.allColumnsData,
          hasReportFormData: !!state?.reportFormData?.columns
        });
        return [];
      }

      const allColumns = state.allColumnsData;

      const groupByFilters = Array.isArray(state.reportFormData.groupByFilters) && state.reportFormData.groupByFilters.length > 0
        ? [state.reportFormData.groupByFilters[0]]
        : [];

      // Graph view uses only the first group-by column plus Y-Axis columns.
      const selectedColumns = Array.from(new Set([...groupByFilters, ...state.reportFormData.columns]));

      // console.log('Transforming data with selected columns only:', selectedColumns);
      // console.log('All columns data:', allColumns);
      // console.log('Report form data columns:', state.reportFormData.columns);
      // console.log('Group by filters:', state.reportFormData.groupByFilters);
      // console.log('Selected columns:', selectedColumns);

      // Debug original data structure
      if (state.reportData.length > 0) {
        // console.log('Original report data sample:', state.reportData[0]);
        // console.log('Original report data length:', state.reportData[0].length);
      }

      // Debug column mapping
      selectedColumns.forEach((columnId: any) => {
        const columnInfo = allColumns[columnId];
        if (columnInfo) {
          // console.log(`Column ID ${columnId} -> Column Name: ${columnInfo.columnName}, Data Type: ${columnInfo.colDataType}`);
        } else {
          // console.log(`Column ID ${columnId} -> No column info found`);
        }
      });

      const reportData = state.reportData.map((item: any, index: number) => {
        const transformedItem: any = {};

        // Process only selected columns
        selectedColumns.forEach((columnId: any, colIndex: number) => {
          const columnInfo = allColumns[columnId];
          if (columnInfo) {
            const columnName = columnInfo.columnName;

            // Check if the item has this column
            if (item[colIndex] !== undefined) {
              // If the value is an object with drilldown_object, extract col_value
              if (typeof item[colIndex] === 'object' && item[colIndex] !== null) {
                // Handle drilldown object
                if (item[colIndex].drilldown_object && item[colIndex].col_value !== undefined) {
                  transformedItem[columnName] = item[colIndex].col_value;
                } else if (item[colIndex].col_value !== undefined) {
                  transformedItem[columnName] = item[colIndex].col_value;
                } else {
                  // If it's an object but no col_value, try to get a string representation
                  transformedItem[columnName] = JSON.stringify(item[colIndex]);
                }
              } else {
                // If it's a direct value, use it as is
                const value = item[colIndex];
                if (columnInfo.colDataType === 1) {
                  transformedItem[columnName] = Number(value) || 0;
                } else {
                  // Ensure string values are properly handled
                  transformedItem[columnName] = value !== null && value !== undefined ? String(value) : '';
                }
              }
            } else {
              // If column doesn't exist in item, set default value
              transformedItem[columnName] = columnInfo.colDataType === 1 ? 0 : '';
            }
          }
        });

        //console.log(`Transformed item ${index}:`, transformedItem);
        return transformedItem;
      });

      //console.log('Final transformed data:', reportData);
      return reportData;
    } catch (error) {
      //console.error('Error transforming report data:', error);
      return [];
    }
  }


  createLegend(root: am5.Root): am5.Legend {
    const legend = am5.Legend.new(root, {
      maxHeight: 50,
      width: am5.percent(100),
      centerX: am5.p50,
      x: am5.p50,
      marginTop: 50,
      marginBottom: 20,
      verticalScrollbar: am5.Scrollbar.new(root, {
        orientation: "vertical"
      }),
      layout: am5.GridLayout.new(root, {
        maxColumns: 4,
        fixedWidthGrid: true,
        //columnWidth: 300
      })
    });

    legend.labels.template.setAll({
      fontSize: 12,
      oversizedBehavior: "truncate",
      maxWidth: 150
    });

    return legend;
  }

  /**
   * Dispose chart to prevent memory leaks
   * @param root - am5.Root instance
   */
  disposeChart(root: am5.Root): void {
    if (root) {
      // Find and unregister the chart from the registry
      const entries = Array.from(BaseChartService.chartRegistry.entries());
      const chartEntry = entries.find(entry => entry[1].root === root);
      if (chartEntry) {
        BaseChartService.unregisterChart(chartEntry[0]);
      }

      root.dispose();
    }
  }

  /**
   * Utility method to safely create chart with DOM element availability checks
   * @param chartId - The ID of the chart container element
   * @param createChartFunction - Function to create the chart
   * @param retryDelay - Delay before retry (default: 100ms)
   * @param maxRetries - Maximum number of retries (default: 10)
   */
  public safeCreateChart(chartId: string, createChartFunction: () => void, retryDelay: number = 100, maxRetries: number = 10): void {
    const chartContainer = document.getElementById(chartId);

    if (chartContainer) {
      // DOM element exists, create chart immediately
      createChartFunction();
    } else {
      // DOM element doesn't exist, retry with exponential backoff
      this.retryChartCreation(chartId, createChartFunction, retryDelay, maxRetries, 0);
    }
  }

  /**
   * Retry chart creation with exponential backoff
   * @param chartId - The ID of the chart container element
   * @param createChartFunction - Function to create the chart
   * @param retryDelay - Base delay before retry
   * @param maxRetries - Maximum number of retries
   * @param currentRetry - Current retry attempt
   */
  private retryChartCreation(chartId: string, createChartFunction: () => void, retryDelay: number, maxRetries: number, currentRetry: number): void {
    if (currentRetry >= maxRetries) {
      console.error(`Chart creation failed after ${maxRetries} retries for chart ID: ${chartId}`);
      return;
    }

    const chartContainer = document.getElementById(chartId);

    if (chartContainer) {
      // DOM element now exists, create chart
      createChartFunction();
    } else {
      // DOM element still doesn't exist, retry with exponential backoff
      const delay = retryDelay * Math.pow(2, currentRetry);
      // console.log(`Chart container not ready for ${chartId}, retrying in ${delay}ms (attempt ${currentRetry + 1}/${maxRetries})`);

      setTimeout(() => {
        this.retryChartCreation(chartId, createChartFunction, retryDelay, maxRetries, currentRetry + 1);
      }, delay);
    }
  }
} 